import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Tab, Tabs, Tooltip, useTheme, Button } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { PageLayout } from '../../PageLayout';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { TabPanel } from '../../Atoms/TabPanel';
import {
  ApiAdvancedFilter,
  FilterableField,
  getSelectedTab,
  PaginatedResponse,
  updateState
} from '../../../store/GeneralStore';
import { Ticket, fetchTickets, getTallyTicketsSubject } from '../../../store/Ticket';
import { getCurrentUser } from '../../../store/User';
import { DefaultTicketGridColumnDefs } from './TicketGridColumnDefs';
import {
  getCustomizationSubject,
  getCustomViewSubject,
  CustomView,
  PrefKey,
  UserCustomizations
} from '../../../store/UserCustomizations';
import {
  buildAdvancedFilterSet,
  getColsForTab,
  getInitialState,
  mergeAndDeduplicateFilters
} from '../../Compounds/UpstackDataGrid/helpers';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { DEFAULT_TAB_SX_STYLES } from '../../../utils/styleHelpers';
import { TFUWidget } from '../../Compounds/Widgets/TFU';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { Menu as CustomViewMenu } from '../../Compounds/CustomTableViews/Menu';
import { clearFiltersFromSearchParams, getFiltersFromSearchParams } from '../../../utils/helpers';

export interface TabProps {
  name: string;
  customView?: boolean;
  filter?: FilterableField[];
  tooltip?: string;
  disabledFilters?: string[];
  isClosedView?: boolean;
}

export const TicketPage = () => {
  const [ticketsLoading, setTicketsLoading] = useState<boolean>(false);
  const [tickets, setTickets] = useState<PaginatedResponse<Ticket>>();
  const [userCustomizations, setUserCustomizations] = useState<UserCustomizations>();
  const [selectedTab, setSelectedTab] = useState(getSelectedTab().tickets);
  const [customViews, setCustomViews] = useState<CustomView[]>([]);
  const [customViewNames, setCustomViewNames] = useState<string[]>([]);
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [selectedClosedView, setSelectedClosedView] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [newlyCreatedViewName, setNewlyCreatedViewName] = useState<string | null>(null);

  const theme = useTheme();
  const filterKeys = DefaultTicketGridColumnDefs.filter((column) => column.filterable).map((column) => column.field);

  const parsedFilters = useMemo(() => getFiltersFromSearchParams(filterKeys), [selectedTab]);

  const defaultTabs = useMemo(
    () =>
      [
        {
          name: 'My Open',
          filter: [
            {
              operator: 'isAnyOf',
              field: 'status',
              value: [
                'New',
                'Pending Partner Response',
                'Monitoring',
                'Pending Source Response',
                'Pending Receipt of Supplier $'
              ]
            },
            { operator: 'equals', field: 'assignedTo.email', value: getCurrentUser()?.email }
          ],
          tooltip: `assignedTo email equals ${getCurrentUser()?.email}
        Status is any of  'New', 'Pending Partner Response', 'Monitoring',
        'Pending Source Response', 'Pending Receipt of Supplier $'
        `,
          disabledFilters: ['status', 'assignedTo.email']
        },
        {
          name: 'Open',
          filter: [
            {
              operator: 'isAnyOf',
              field: 'status',
              value: [
                'New',
                'Pending Partner Response',
                'Monitoring',
                'Pending Source Response',
                'Pending Receipt of Supplier $'
              ]
            }
          ],
          tooltip:
            "Status is any of  'New', 'Pending Partner Response', 'Monitoring', 'Pending Source Response', 'Pending Receipt of Supplier $'",
          disabledFilters: ['status']
        },
        {
          name: 'My Overdue',
          filter: [
            { operator: 'before', field: 'expectedResolutionDate', value: new Date().toISOString() },
            { operator: 'isEmpty', field: 'resolutionType', value: undefined },
            { operator: 'equals', field: 'assignedTo.email', value: getCurrentUser()?.email }
          ],
          tooltip: `expectedResolutionDate is before ${new Date().toDateString()}
      resolutionType is undefined
      assignedTo email equals ${getCurrentUser()?.email}
      `,
          disabledFilters: ['expectedResolutionDate', 'resolutionType', 'assignedTo.email']
        },
        {
          name: 'Overdue',
          filter: [
            { operator: 'before', field: 'expectedResolutionDate', value: new Date().toISOString() },
            { operator: 'isEmpty', field: 'resolutionType', value: undefined }
          ],
          tooltip: `expectedResolutionDate is before ${new Date().toDateString()}
      resolutionType is undefined
      `,
          disabledFilters: ['expectedResolutionDate', 'resolutionType']
        },
        {
          name: 'Recent',
          filter: [
            {
              operator: 'onOrAfter',
              field: 'updatedAt',
              value: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
            }
          ],
          tooltip: `updatedAt is on or after ${new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toDateString()}
      resolutionType is undefined
      `,
          disabledFilters: ['expectedResolutionDate', 'resolutionType']
        },
        {
          name: 'All',
          filter: [],
          tooltip: 'Unfiltered',
          disabledFilters: []
        }
      ] as TabProps[],
    []
  );

  const closedViews = useMemo(
    () =>
      [
        {
          name: 'My Closed',
          filter: [
            {
              operator: 'isAnyOf',
              field: 'status',
              value: ['Pending Advisor Call', 'Resolved', 'Closed - No Response', 'Duplicate']
            },
            { operator: 'equals', field: 'assignedTo.email', value: getCurrentUser()?.email }
          ],
          tooltip: `assignedTo email equals ${getCurrentUser()?.email}
      Status is any of 'Pending Advisor Call', 'Resolved', 'Closed - No Response', 'Duplicate'
      `,
          disabledFilters: ['status', 'assignedTo.email'],
          isClosedView: true
        },
        {
          name: 'Closed',
          filter: [
            {
              operator: 'isAnyOf',
              field: 'status',
              value: ['Pending Advisor Call', 'Resolved,Closed - No Response', 'Duplicate']
            }
          ],
          tooltip: "Status is any of 'Pending Advisor Call', 'Resolved', 'Closed - No Response', 'Duplicate'",
          disabledFilters: ['status'],
          isClosedView: true
        }
      ] as TabProps[],
    []
  );

  const allAvailableViews = useMemo(() => {
    const customViewsTabProps: TabProps[] = customViews
      .filter((view) => view.page === 'ticketGrid_')
      .map((t) => ({ name: t.name, customView: true }));

    return [...defaultTabs, ...closedViews, ...customViewsTabProps];
  }, [defaultTabs, closedViews, customViews]);

  const mainTabsToDisplay = useMemo(() => {
    return defaultTabs.filter((tab) => !tab.isClosedView);
  }, [defaultTabs]);

  const closedMenuOptions = useMemo(() => {
    return closedViews.map((view) => {
      return { name: view.name, tooltip: view.tooltip };
    });
  }, [closedViews]);

  const handleFetchTickets = useCallback(
    async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
      if (localLoading) setTicketsLoading(true);

      let currentTabFilters: FilterableField[] = [];
      const currentSelectedView = allAvailableViews[selectedTab];

      if (currentSelectedView) {
        currentTabFilters = (currentSelectedView.filter as FilterableField[]) || [];
      }

      await fetchTickets(
        {
          ...filters,
          filters: mergeAndDeduplicateFilters(filters?.filters || [], currentTabFilters, true)
        },
        forceRefresh
      );
      if (localLoading) setTicketsLoading(false);
    },
    [allAvailableViews, selectedTab]
  );

  const handleTabChange = useCallback(
    (index: number, name: string, type: 'main' | 'custom' | 'closed') => {
      if (type === 'main') {
        setSelectedCustomView('');
        setSelectedClosedView('');
      } else if (type === 'custom') {
        setSelectedCustomView(name);
        setSelectedClosedView('');
      } else if (type === 'closed') {
        setSelectedClosedView(name);
        setSelectedCustomView('');
      }

      const actualIndex = allAvailableViews.findIndex((tab) => {
        if (type === 'custom') {
          return tab.name === name && tab.customView;
        } else if (type === 'closed') {
          return tab.name === name && tab.isClosedView;
        } else {
          return tab.name === name && !tab.customView && !tab.isClosedView;
        }
      });

      if (actualIndex !== -1) {
        updateState({
          selectedTab: {
            ...getSelectedTab(),
            tickets: actualIndex
          }
        });
        setSelectedTab(actualIndex);
      }
    },
    [allAvailableViews]
  );

  const getInitialSort = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `ticketGrid_${pageName}` as PrefKey;
      const savedSort = userCust?.content?.[currentPage]?.muiConfig?.sorting?.sortModel;
      return savedSort?.length ? savedSort : ([] as GridSortModel);
    },
    [allAvailableViews, selectedTab]
  );

  const getInitialFilter = useCallback(
    (userCust: UserCustomizations) => {
      if (parsedFilters.length > 0) {
        clearFiltersFromSearchParams();
        return { items: parsedFilters, logicOperator: 'and' } as GridFilterModel;
      }
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `ticketGrid_${pageName}` as PrefKey;
      const savedFilter = userCust?.content?.[currentPage]?.muiConfig?.filter?.filterModel;
      return savedFilter || ({} as GridFilterModel);
    },
    [allAvailableViews, selectedTab]
  );

  const dataFetch = useCallback(() => {
    if (userCustomizations && allAvailableViews[selectedTab]) {
      const initialSortModel = getInitialSort(userCustomizations);
      const initialFilterModel = getInitialFilter(userCustomizations);
      handleFetchTickets(
        {
          ...buildAdvancedFilterSet(initialFilterModel || ({} as GridFilterModel), filterKeys),
          orders: initialSortModel
            ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
            : undefined
        },
        true,
        true
      );
    }
  }, [
    userCustomizations,
    allAvailableViews,
    selectedTab,
    getInitialSort,
    getInitialFilter,
    handleFetchTickets,
    filterKeys
  ]);

  const handleNewViewCreated = useCallback((viewName: string) => {
    setNewlyCreatedViewName(viewName);
  }, []);

  useEffect(() => {
    const ticketSub = getTallyTicketsSubject().subscribe((a) => setTickets(a));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => setUserCustomizations(userCust));
    const customViewsSub = getCustomViewSubject().subscribe((custViews) => setCustomViews(custViews));

    return () => {
      if (ticketSub) ticketSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
      if (customViewsSub) customViewsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setCustomViewNames(customViews.filter((view) => view.page === 'ticketGrid_').map((v) => v.name));
  }, [customViews]);

  useEffect(() => {
    if (allAvailableViews.length > 0 && selectedTab !== undefined && selectedTab >= 0) {
      const initialSelectedView = allAvailableViews[selectedTab];
      if (initialSelectedView) {
        if (initialSelectedView.customView) {
          setSelectedCustomView(initialSelectedView.name);
          setSelectedClosedView('');
        } else if (initialSelectedView.isClosedView) {
          setSelectedClosedView(initialSelectedView.name);
          setSelectedCustomView('');
        } else {
          setSelectedCustomView('');
          setSelectedClosedView('');
        }
      }
    }
  }, [allAvailableViews, selectedTab]);

  useEffect(() => {
    if (newlyCreatedViewName) {
      const newViewIndex = allAvailableViews.findIndex((tab) => tab.name === newlyCreatedViewName && tab.customView);
      if (newViewIndex !== -1) {
        handleTabChange(newViewIndex, newlyCreatedViewName, 'custom');
        setNewlyCreatedViewName(null);
      }
    }
  }, [newlyCreatedViewName, allAvailableViews, handleTabChange]);

  useEffect(() => {
    if (userCustomizations && allAvailableViews.length > 0) {
      dataFetch();
    }
  }, [selectedTab, userCustomizations, allAvailableViews]);

  useEffect(() => {
    if (selectedCustomView && !customViewNames.includes(selectedCustomView)) {
      setSelectedCustomView('');
      const allTabIndex = allAvailableViews.findIndex(
        (tab) => tab.name === 'All' && !tab.customView && !tab.isClosedView
      );
      handleTabChange(allTabIndex !== -1 ? allTabIndex : 0, 'All', 'main');
    }
    const closedViewNames = closedMenuOptions.map((view) => view.name);
    if (selectedClosedView && !closedViewNames.includes(selectedClosedView)) {
      setSelectedClosedView('');
      const allTabIndex = allAvailableViews.findIndex(
        (tab) => tab.name === 'All' && !tab.customView && !tab.isClosedView
      );
      handleTabChange(allTabIndex !== -1 ? allTabIndex : 0, 'All', 'main');
    }
  }, [selectedCustomView, selectedClosedView, customViewNames, closedMenuOptions, allAvailableViews, handleTabChange]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleNewTabModalOpen = () => {
    setCreateOpen(true);
  };

  const handleCustomViewMenuChange = useCallback(
    (name: string) => {
      const index = allAvailableViews.findIndex((tab) => tab.name === name && tab.customView);
      if (index !== -1) {
        handleTabChange(index, name, 'custom');
      }
    },
    [allAvailableViews, handleTabChange]
  );

  const handleClosedMenuChange = useCallback(
    (name: string) => {
      const index = allAvailableViews.findIndex((tab) => tab.name === name && tab.isClosedView);
      if (index !== -1) {
        handleTabChange(index, name, 'closed');
      }
    },
    [allAvailableViews, handleTabChange]
  );

  return (
    <PageLayout
      dataCy="ticket-page"
      dataTestId="Ticket-page"
      pageTitle="Tickets">
      <Fragment>
        <TFUWidget />
        <Tabs
          sx={DEFAULT_TAB_SX_STYLES}
          value={selectedTab}
          variant="scrollable"
          scrollButtons="auto"
          onChange={(_e, index) => {
            const currentTab = allAvailableViews[index];
            if (currentTab) {
              if (currentTab.customView) {
                handleTabChange(index, currentTab.name, 'custom');
              } else if (currentTab.isClosedView) {
                handleTabChange(index, currentTab.name, 'closed');
              } else {
                handleTabChange(index, currentTab.name, 'main');
              }
            }
          }}
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: theme.palette.primary.main,
                height: allAvailableViews[selectedTab]?.customView || selectedClosedView ? 0 : 2,
                borderRadius: 1
              }
            }
          }}>
          {mainTabsToDisplay.map((tab, _index) => (
            <Tab
              sx={{
                color: 'grey9',
                fontSize: '.8rem',
                textTransform: 'none'
              }}
              key={tab.name}
              label={
                <Tooltip
                  title={<span style={{ whiteSpace: 'pre-line' }}>{tab.tooltip}</span>}
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 'none',
                        width: 'auto'
                      }
                    }
                  }}>
                  <span>{tab.name}</span>
                </Tooltip>
              }
            />
          ))}
          <div className={`flex ${selectedClosedView ? 'border-b-2 border-indigo' : ''}`}>
            <div
              key="closed-views"
              className="self-center">
              <CustomViewMenu
                setSelectedView={setSelectedClosedView}
                selectedView={selectedClosedView}
                viewOptions={closedMenuOptions}
                handleViewChange={handleClosedMenuChange}
                defaultTitle="Closed"
              />
            </div>
          </div>
        </Tabs>
        <div className="-mt-12 float-right inline-flex pr-4">
          <div className={`flex ${selectedCustomView ? 'border-b-2 border-indigo' : ''}`}>
            <div
              key="custom-views"
              className="self-center">
              <CustomViewMenu
                setSelectedView={setSelectedCustomView}
                selectedView={selectedCustomView}
                viewOptions={customViewNames}
                handleViewChange={handleCustomViewMenuChange}
                defaultTitle="My Views"
              />
            </div>
          </div>
          <div className="ml-3 self-center">
            <Button
              onClick={() => setTriggerSave(true)}
              startIcon={<AddCircleIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Save As View
            </Button>
          </div>
          <div
            className="ml-3 self-center"
            key="add-custom-view">
            <Button
              onClick={() => setOpen(true)}
              startIcon={<EditNoteIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Edit Views
            </Button>
          </div>
        </div>
        {allAvailableViews.map((tab, index) => {
          let tabKeyName = tab.name || '';
          if (tab && !tab.customView) {
            tabKeyName = tabKeyName.replace(/\s/g, '');
          }
          const tabKey = `ticketGrid_${tabKeyName}` as PrefKey;
          const colsForTab = getColsForTab(DefaultTicketGridColumnDefs, tab.disabledFilters ?? []);
          const gridSettingsForTab = userCustomizations?.content?.[tabKey];
          let initialState;
          if (gridSettingsForTab) {
            initialState = getInitialState(gridSettingsForTab, colsForTab, tabKey);
            const initialFilterModel = getInitialFilter(userCustomizations);
            if (initialState && initialFilterModel) {
              if (initialState.filter) {
                initialState.filter.filterModel = initialFilterModel;
              } else {
                initialState.filter = { filterModel: initialFilterModel };
              }
            }
          }
          return (
            <Fragment key={tab.name}>
              {!initialState ||
                (ticketsLoading && (
                  <div className="flex h-[45rem] m-auto">
                    <ComponentSpinner label="Loading data..." />
                  </div>
                ))}
              {initialState && !ticketsLoading && (
                <TabPanel
                  key={tab.name}
                  index={index}
                  value={selectedTab}>
                  <UpstackDataGrid
                    advancedFiltering={true}
                    apiFilter={true}
                    columns={colsForTab}
                    fetchFunction={handleFetchTickets}
                    filterKeys={filterKeys}
                    gridSettings={gridSettingsForTab}
                    handleNewTabModalOpen={handleNewTabModalOpen}
                    initialState={initialState}
                    page={tabKey}
                    rows={tickets?.data || []}
                    rowCount={tickets?.totalCount}
                    showSearch={false}
                    title="Tickets"
                    triggerSave={triggerSave}
                    handleSaveComplete={() => setTriggerSave(false)}
                  />
                </TabPanel>
              )}
            </Fragment>
          );
        })}
      </Fragment>
      <ViewsModal
        open={open}
        handleClose={handleClose}
        type="ticket"
      />
      <NewViewModal
        onViewCreated={handleNewViewCreated}
        open={createOpen}
        setOpen={setCreateOpen}
        sourceTab={allAvailableViews[selectedTab]?.name || 'All'}
        sourceTabFilter={allAvailableViews[selectedTab]?.filter}
        type="ticket"
      />
    </PageLayout>
  );
};
